import { memo, useMemo, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

interface StepsProgressBarProps {
  currentStep: number;
  labels: string[];
}

function StepsProgressBarInner({
  currentStep,
  labels,
}: StepsProgressBarProps) {
  const { i18n } = useTranslation();
  const rtlFlow = i18n.dir() === "rtl";

  const stepStates = useMemo(
    () =>
      labels.map((_, index) => {
        if (index < currentStep) return "completed" as const;
        if (index === currentStep) return "current" as const;
        return "upcoming" as const;
      }),
    [labels, currentStep],
  );

  return (
    <nav
      className={`registration-steps-progress mb-4${
        rtlFlow ? " registration-steps-progress--rtl-flow" : ""
      }`}
      dir="ltr"
      aria-label="Progress"
    >
      <ol
        className="registration-steps-progress__list list-unstyled d-flex mb-0 w-100 position-relative"
        style={{ "--step-count": labels.length } as CSSProperties}
      >
        <div className="registration-steps-progress__rail" aria-hidden />
        {labels.map((label, index) => {
          const state = stepStates[index];

          return (
            <li
              key={`step-${index}`}
              className="registration-steps-progress__item flex-grow-1 d-flex flex-column align-items-center position-relative"
            >
              <span
                className={`registration-steps-progress__badge rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 registration-steps-progress__badge--${state}`}
                aria-current={state === "current" ? "step" : undefined}
              >
                {state === "completed" ? (
                  <i className="bi bi-check-lg" aria-hidden />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={`registration-steps-progress__label small mt-2 px-1 registration-steps-progress__label--${state}`}
                dir="auto"
              >
                <span className="d-none d-sm-inline">{label}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

StepsProgressBarInner.displayName = "StepsProgressBar";

const StepsProgressBar = memo(StepsProgressBarInner);
StepsProgressBar.displayName = "StepsProgressBar";

export default StepsProgressBar;
